import { getField, updateField } from 'vuex-map-fields';
import donationWayType from '@/configs/donationWay';
import receiptType from '@/configs/receiptType';
import donationPeriodType from '@/configs/donationPeriod';
import _ from 'lodash';
import * as Sentry from '@sentry/vue';
import ECPay from './donationECPay';
import LinePay from './donationLinePay';
import JKOPay from './donationJKOPay';
import {
  getRecurringDonationOrderNumber,
  newRecurringDonation,
  newOpportunity,
  validateCampaignId,
} from '@/api';

import {
  getDonationDefaultState,
} from '@/configs/donationDefaultValues';

export default {
  namespaced: true,

  modules: {
    ECPay,
    LinePay,
    JKOPay,
  },

  state: getDonationDefaultState(),

  mutations: {
    updateField,
    submittingSuccess(state, message) {
      state.submittingMessage = message;
      state.submitted = true;
      state.submittedError = false;
    },
    submittingError(state, message) {
      state.submittingMessage = message;
      state.submitted = true;
      state.submittedError = true;
    },
    resetFields(state) {
      // workaround: 無法直接用 donationDefaultValues reset
      // 之後有時間再追
      state.donationPeriod = donationPeriodType.DONATION_SINGLE; // 單次捐款 或是 定期定額 （前端自定義欄位）
      state.donationOwner = ''; // 捐款名義 (個人名義/團體名義/匿名)
      state.donationWay = ''; // 捐款方式 (ATM/LINE/信用卡)

      state.amount = ''; // 捐款金額
      state.isAnonymous = ''; // 是否為匿名 True/False
      state.showOnDonationList = false; // 是否要在捐贈名冊上顯示名稱
      state.preferredNotification = true; // 是否要收到文宣
      state.campaignId = '';
      state.contact = {
        lastName: '', // 聯絡人姓氏,
        firstName: '', // 聯絡人名字
        birthDate: '', // 出生年月日（個人名義）
        email: '', // Email
        phone: '', // 手機號碼

        groupName: '', // 組織名稱
      };

      state.receipt = {
        type: '', // 收據: "寄送單筆電子收據", "不需要收據", or "寄送年度紙本收據"
        title: '', // 收據寄發抬頭 (定期定額)
        idNo: '', // 收據: 統編/身分證字號
        etaxCheck: false, // 自動上傳國稅局抵減所得稅額 True/False (只有個人有)
        mailingCity: '', // 縣市  台北市
        mailingDistrict: '', // 鄉鎮市區 信義區
        mailingZipCode: '', //  100
        mailingAddress: '', // 地址 松仁路7號
      };

      state.creditCard = {
        ownerName: '', // 持卡人姓名"
        cardNumber: '', // 信用卡號16位數字
        expiredTime: '', // 有效年月MMYY
      };

      state.submitted = false;
      state.submittingMessage = '';
      state.submittedError = false;
    },
  },

  getters: {
    getField,
    oppoName(state, getters) { // API需要的欄位值
      const { contact } = state;
      switch(state.donationOwner) {
        case '個人名義':
          return '官網單筆捐款' + ' ' + contact.lastName + contact.firstName + ' ' + getters.nowDate;
        case '匿名':
          return '官網單筆捐款' + ' ' + '匿名' + ' ' + getters.nowDate;
        case '團體名義':
          return '官網單筆捐款' + ' ' + contact.groupName + ' ' + getters.nowDate;
      }
    },

    isGroup(state) { // API需要的欄位值
      switch(state.donationOwner) {
        case '個人名義':
        case '匿名':
          return false;

        case '團體名義':
          return true;
      }
    },

    isAnonymous(state) {
      return state.donationOwner === '匿名';
    },

    nowDate() {
      const date = new Date();
      const dd = date.getDate();
      const mm = date.getMonth() + 1;
      const yyyy = date.getFullYear();
      const nowDate = yyyy + '/' + mm + '/' + dd;
      return nowDate;
    },

    invoiceType(state) { // 收據類型：API需要的欄位值
      switch(state.receipt.type) {
        case receiptType.SINGLE_ELEC_RECEIPT:
          return 'Single_e';
        case receiptType.SINGLE_PAPER_RECEIPT:
          return 'Single_paper';
        case receiptType.YEAR_ELEC_RECEIPT:
          return 'Year_e';
        case receiptType.YEAR_PAPER_RECEIPT:
          return 'Year_paper';
        case '不需要收據':
          return 'No_Need';
      }
    },

    preferredDonationListDisplayName(state) { // 捐贈名冊姓名顯示類型
      switch(state.showOnDonationList) {
        case '全名（如：王大明）':
          return 'fullName';
        case '去識別化（如：王O明）':
          return 'anonymous';
      }
    },

    orderType(state) {
      switch(state.donationWay) {
        case donationWayType.ECPAY_SINGLE_CREDIT:
          return 'CREDIT';
        case donationWayType.ECPAY_PERIOD_CREDIT:
          return 'CREDIT-PERIOD';
        case donationWayType.JKOPAY_SINGLE:
          return 'JKO';
        case donationWayType.ECPAY_SINGLE_ATM:
          return 'ATM';
        case donationWayType.ECPAY_SINGLE_CVS:
          return 'CVS';
        case donationWayType.LINEPAY_SINGLE:
          return 'LINE';
      }
    },

    donationInfo(state) {
      const fields = [];

      fields.push({
        name: '捐款類型',
        value: state.donationPeriod,
      });

      fields.push({
        name: '捐款金額',
        value: 'NT$' + state.amount,
      });

      fields.push({
        name: '捐款方式',
        value: state.donationWay,
      });

      fields.push({
        name: '捐款結果',
        value: state.submittingMessage,
      });

      return fields;
    },

    anonymousFields() {
      const fields = [];

      fields.push({
        name: '捐款單位',
        value: '匿名',
      });

      return fields;
    },

    // 個人資料
    singleFields(state) {
      const contact = state.contact;
      const fields = [
        {
          name: '聯絡人姓氏',
          value: contact.lastName,
        },
        {
          name: '聯絡人名字',
          value: contact.firstName,
        },
        {
          name: '生日',
          value: contact.birthDate,
        },
        {
          name: 'Email',
          value: contact.email,
        },
        {
          name: '手機號碼',
          value: contact.phone,
        },
      ];

      return fields;
    },

    // 團體資料
    groupFields(state) {
      const contact = state.contact;
      const receipt = state.receipt;

      const fields = [
        {
          name: '單位名稱',
          value: contact.groupName,
        },
        {
          name: '統編',
          value: receipt.idNo,
        },
        {
          name: '聯絡人姓氏',
          value: contact.lastName,
        },
        {
          name: '聯絡人名字',
          value: contact.firstName,
        },
        {
          name: 'Email',
          value: contact.email,
        },
        {
          name: '手機號碼',
          value: contact.phone,
        },
      ];
      return fields;
    },

    // 發票資料
    receiptFields(state) {
      const fields = [];
      const receipt = state.receipt;

      fields.push({
        name: '收據種類',
        value: receipt.type,
      });

      if(receipt.type === '不需要收據') {
        return fields;
      }

      fields.push({
        name: '收據抬頭',
        value: receipt.title,
      });

      fields.push({
        name: '收據統編/身分證字號',
        value: receipt.idNo,
      });

      if(state.donationOwner === '個人名義') {
        fields.push({
          name: '是否同意為您上傳捐款資料至國稅局抵減所得稅額？',
          value: receipt.etaxCheck ? '是' : '否',
        });
      }

      if(receipt.type === receiptType.SINGLE_PAPER_RECEIPT || receipt.type === receiptType.YEAR_PAPER_RECEIPT) {
        fields.push({
          name: '收據寄發地址',
          value: receipt.mailingZipCode + receipt.mailingCity + receipt.mailingDistrict + receipt.mailingAddress,
        });
      }

      return fields;
    },

    otherFields(state) {
      const fields = [];
      fields.push({
        name: '我同意 TFT 依財團法人法第 25 條公開捐贈者姓名及金額之規定(若不同意公開，須依照說明簽署與回傳聲明書)',
        value: '是',
      });

      fields.push({
        name: '我已詳閱並同意「為台灣而教教育基金會個資蒐集同意書」',
        value: '是',
      });

      fields.push({
        name: '我願意收到 TFT 相關資訊',
        value: state.preferredNotification ? '是' : '否',
      });

      return fields;
    },

  },

  actions: {
    // 創建單次捐款 (適用於街口 / 綠界 / LINE 支付)
    async createSingleDonate({ state, getters, commit, dispatch }) {
      const { contact, receipt } = state;
      const { isGroup } = getters;
      const contactIsAnonymous = state.donationOwner === '匿名';

      let payload = {
        oppoName: getters.oppoName,
        oppoAmount: state.amount,
        contactIsAnonymous: contactIsAnonymous,
        contactEtaxCheck: receipt.etaxCheck,
        oppoOrderType: getters.orderType,
      };

      if(state.campaignId) {
        payload = _.assign({}, payload, {
          contactCampaignId: state.campaignId,
        });
      }

      if(!contactIsAnonymous) {
        payload = _.assign({}, payload, {
          contactLastName: contact.lastName,
          contactFirstName: contact.firstName,
          contactIsGroup: isGroup,
          contactEmail: contact.email,
          contactPhone: contact.phone,
          // 收據寄送資料
          conatctInvoiceType: getters.invoiceType, // 收據種類

          // 希望在捐冊名冊上的名字顯示方式
          contactDonationListDisplayName: getters.preferredDonationListDisplayName,
          // 是否要收到文宣
          // todo: 後端要改成 true / false
          contactPreferredNotification: state.preferredNotification,
        });

        if(receipt.title) {
          payload.oppoInvoiceTitle = receipt.title;
          payload.contactInvoiceTitle = receipt.title; // 收據抬頭
        }

        if(receipt.idNo) {
          payload.contactReceiptIdNo = receipt.idNo; // 收據身分證/統編
        }

        if(receipt.type === receiptType.YEAR_PAPER_RECEIPT || receipt.type === receiptType.SINGLE_PAPER_RECEIPT) { // 只有年度紙本收據才需要收地址
          payload.contactMailingCity = receipt.mailingCity;
          payload.contactMailingStreet = receipt.mailingDistrict + receipt.mailingAddress;
          payload.contactZipCode = receipt.mailingZipCode;
        }

        if(isGroup) { // 團體
          payload.contactGroupTitle = contact.groupName;
        } else if(contact.birthDate) { // 個人
          payload.contactBirthdate = contact.birthDate;
        }
      }

      const orderType = getters.orderType;

      // 1. 創建機會 ID
      try {
        const result = await newOpportunity(payload);
        const oppoResult = result.data;
        const oppoId = oppoResult['oppoID'];
        if(orderType === 'LINE') { // 如果是LINE Pay則獲取機會ID
          commit('donation/LinePay/updateLinePayOrderid', oppoId, { root: true });
        } else if (orderType === 'JKO') { // 街口支付
          commit('donation/JKOPay/updatePlatformOrderId', oppoId, { root: true });
        } else { // 如果是綠界，更新 oppoid
          commit('donation/ECPay/updateSingleOrderId', oppoId, { root: true });
        }
      } catch(e) {
        throw new Error(e);
      }

      // 2. 獲取 Token
      if (orderType === 'ATM' || orderType === 'CVS' || orderType === 'CREDIT') {
        try {
          await dispatch('donation/ECPay/getECPayToken', {}, { root: true });
        } catch(err) {
          const errorMessage = new Error('API Error(/getECPayToken.php): ' + err);
          Sentry.captureException(errorMessage);
          alert(errorMessage + '\n (捐款失敗，請將此錯誤訊息進行截圖，並將截圖寄信至 fin@teach4taiwan.org，將會由專人協助處理，謝謝)');
        }
      }

      return true;
    },
    // 定期定額捐款
    async createRegularDonate({ state, getters, commit, dispatch }) {

      let orderId = '';

      // 1. 獲取定期定額編號
      try {
        const resp = await getRecurringDonationOrderNumber();
        orderId = resp.data['oppoID'];
        if(state.donationWay === donationWayType.ECPAY_PERIOD_CREDIT) {
          commit('donation/ECPay/updateRegularOrderId', orderId, { root: true });
        } else if (state.donationWay === donationWayType.LINEPAY_PERIOD) {
          commit('donation/LinePay/updateLinePayOrderid', orderId, { root: true });
        }
      } catch(err) {
        const errorMessage = new Error('API Error(/newOrderNumber.php): ' + err);
        Sentry.captureException(errorMessage);
        alert(errorMessage + '\n (捐款失敗，請將此錯誤訊息進行截圖，並將截圖寄信至 fin@teach4taiwan.org，將會由專人協助處理，謝謝)');
      }

      // 2. 獲取綠界 Token
      if (state.donationWay === donationWayType.ECPAY_PERIOD_CREDIT) {
        try {
          await dispatch('donation/ECPay/getECPayToken', {}, { root: true });
        } catch(err) {
          const errorMessage = new Error('API Error(/getECPayToken.php): ' + err);
          Sentry.captureException(errorMessage);
          alert(errorMessage + '\n (捐款失敗，請將此錯誤訊息進行截圖，並將截圖寄信至 fin@teach4taiwan.org，將會由專人協助處理，謝謝)');
        }
      }

      // 3. 創建定期定額
      const { contact, receipt } = state;
      const { isGroup } = getters;
      const contactIsGroup = (!isGroup && state.donationOwner === '匿名')
        ? 'anonymous'
        : isGroup;

      const payload = {
        recurDonationPayType: state.donationWay === donationWayType.LINEPAY_PERIOD ? 'LINE_PAY' : 'ECPAY',
        recurDonationInstallmentAmount: state.amount,
        recurDonationOrderId: orderId,
        contactIsGroup: contactIsGroup, // 是否為團體名義 True/False/anonymous
        ...(isGroup && { contactGroupTitle: contact.groupName }), // 團體
        ...((state.donationOwner === '個人名義' && contact.birthDate) && { contactBirthdate: contact.birthDate }), // 個人
        contactLastName: contact.lastName,
        contactFirstName: contact.firstName,
        contactEmail: contact.email,
        contactPhone: contact.phone,
        // 收據寄送資料
        conatctInvoiceType: getters.invoiceType, // 收據種類
        contactEtaxCheck: receipt.etaxCheck,

        // 希望在捐款名冊上的名字顯示方式
        contactDonationListDisplayName: getters.preferredDonationListDisplayName,
        // todo: 後端要改成 true / false
        contactPreferredNotification: state.preferredNotification,
      };

      if(receipt.type === receiptType.YEAR_PAPER_RECEIPT || receipt.type === receiptType.SINGLE_PAPER_RECEIPT) { // 只有紙本收據才需要收地址
        payload.contactMailingCity = receipt.mailingCity;
        payload.contactMailingStreet = receipt.mailingDistrict + receipt.mailingAddress;
        payload.contactZipCode = receipt.mailingZipCode;
      }

      if(receipt.title) {
        payload.contactInvoiceTitle = receipt.title; // 收據抬頭，需要才送
      }

      if(receipt.idNo) {
        payload.contactReceiptIdNo = receipt.idNo; // 收據身分證/統編，需要才送
      }

      if(state.campaignId) { // 如果有活動id ，才送到後端
        payload.contactCampaignId = state.campaignId;
      }

      try {
        const result = await newRecurringDonation(payload);
        if(result.data?.rtnStatus === 'Success') {
          return true;
        } else if(result.data?.rtnStatus === 'Fail') {
          throw new Error(result.data?.msg || '更新失敗');
        }
      } catch(err) {
        const errorMessage = new Error('API Error(/newRecurringDonation.php): ' + 'payload: ' + JSON.stringify(payload) + ' error: ' + err);
        Sentry.captureException(errorMessage);
        alert(errorMessage + '\n (捐款失敗，請將此錯誤訊息進行截圖，並將截圖寄信至 fin@teach4taiwan.org，將會由專人協助處理，謝謝)');
      }
    },

    async validateCampaignId(_, campaignId) {
      const payload = {
        campaignId: campaignId,
      };
      const resp = await validateCampaignId(payload);
      const validate = resp.data.rtnCode == 1;
      return validate;
    },

    resetFields({ commit }) {
      commit('resetFields');
    },
  },
};
